import type { CapabilityState, CloudflareAccessUser } from "../shared/types";
import { formatDate } from "./admin-model";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { PaginationControls, usePagination } from "./pagination";

export function CloudflareSessionsSection({
  activeSessions,
  capability,
  users,
  onRevoke,
}: {
  activeSessions: number;
  capability: CapabilityState;
  users: CloudflareAccessUser[];
  onRevoke: (user: CloudflareAccessUser) => void;
}) {
  const [pageSlice, setPage] = usePagination(users, 15);
  return (
    <section className="directory-card cloudflare-section">
      <header className="collection-header">
        <div>
          <h2>Active Access sessions</h2>
          <p>Account-wide sessions and their protected application reach.</p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <span className="collection-header__count">{activeSessions}</span>
        </div>
      </header>
      <div className="table-wrap">
        <table className="cloudflare-sessions-table">
          <caption className="sr-only">Cloudflare Access user sessions</caption>
          <thead>
            <tr>
              <th>User</th>
              <th>Sessions</th>
              <th>Applications</th>
              <th>Expires</th>
              <th>Devices</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageSlice.items.map((user) => {
              const applications = new Set(
                user.sessions.flatMap((session) =>
                  session.applications.map(
                    (application) => application.hostname,
                  ),
                ),
              );
              const latestExpiry = Math.max(
                0,
                ...user.sessions.map((session) => session.expiresAt ?? 0),
              );
              return (
                <tr key={user.id}>
                  <td data-label="User">
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                  </td>
                  <td data-label="Sessions">{user.sessions.length}</td>
                  <td data-label="Applications">
                    {applications.size || "None recorded"}
                  </td>
                  <td data-label="Expires">
                    {latestExpiry
                      ? formatDate(latestExpiry)
                      : "No active session"}
                  </td>
                  <td data-label="Devices">{user.activeDeviceCount}</td>
                  <td data-label="Actions">
                    <button
                      className="button button--secondary button--compact"
                      disabled={!user.sessions.length}
                      onClick={() => onRevoke(user)}
                      type="button"
                    >
                      Revoke user
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!users.length && (
          <EmptyState
            hint="Sessions appear after users authenticate to Access."
            title="No Access users loaded"
          />
        )}
      </div>
      <PaginationControls
        label="Access users"
        onPageChange={setPage}
        slice={pageSlice}
      />
    </section>
  );
}
